/**
 * The three chart scales, as pure functions (Task 13 review M2).
 *
 * WHY THIS FILE EXISTS. `role="img"` plus a non-empty `aria-label` plus a
 * matching `<title>` plus "no NaN in the markup" proves the BOX exists. It does
 * not prove the PICTURE is true, and the review demonstrated that this is not a
 * theoretical distinction: an inverted y-axis (every peak drawn as a trough), a
 * bar height divided by the point COUNT instead of the maximum, and a ratio bar
 * drawing the complement of the number printed beside it all survived the full
 * suite with finite coordinates and a truthful axis label.
 *
 * For a metrics view that matters more than for most surfaces, because reading
 * a SHAPE at a glance is the whole reason the chart is there -- the numbers are
 * in the tables. A chart that draws every rise as a fall is worse than no chart.
 *
 * So the geometry is a leaf with three properties pinned in Node
 * (test/chartScale.test.ts), and no fourth DOM suite is needed -- the phase's
 * cap of three is spent:
 *
 *   1. ANCHORS      — 0 sits on the baseline, `max` sits at the top.
 *   2. MONOTONICITY — a bigger value is drawn HIGHER (a smaller `y`) and TALLER.
 *   3. CONTAINMENT  — every output stays inside the plot box.
 *
 * The components call these and compute nothing of their own, so a call site
 * that stops calling them is what the DOM suite's ordering assertions catch.
 */

/** One shared box for both cartesian charts, so their axes are comparable. */
export const CHART_WIDTH = 640;
export const CHART_HEIGHT = 180;
export const CHART_PAD_X = 8;
export const CHART_PAD_TOP = 12;
export const CHART_PAD_BOTTOM = 28;
export const CHART_PLOT_HEIGHT = CHART_HEIGHT - CHART_PAD_TOP - CHART_PAD_BOTTOM;
/** The y of the value 0 — the axis line both charts sit on. */
export const CHART_BASELINE = CHART_PAD_TOP + CHART_PLOT_HEIGHT;

export const RATIO_WIDTH = 640;
export const RATIO_HEIGHT = 34;

/** `max`, floored at 1 so an all-zero series still has a scale rather than a
 *  division by zero. Non-finite values cannot reach here (lib/metrics.ts drops
 *  and counts them) and are floored out anyway. */
export function chartMax(values: readonly number[]): number {
  let max = 1;
  for (const value of values) if (Number.isFinite(value) && value > max) max = value;
  return max;
}

/**
 * A value -> its y coordinate. SVG's y grows DOWNWARDS, which is the whole
 * hazard: `PAD_TOP + (v/max)*plotHeight` is the same expression with the axis
 * inverted, produces finite coordinates, and draws every peak as a trough.
 */
export function plotY(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return CHART_BASELINE;
  const clamped = Math.max(0, Math.min(value, max));
  return CHART_PAD_TOP + CHART_PLOT_HEIGHT - (clamped / max) * CHART_PLOT_HEIGHT;
}

/** A value -> its bar height. The complement of `plotY`, and it must be scaled
 *  by the MAXIMUM, never by the number of points. */
export function barHeight(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
  const clamped = Math.max(0, Math.min(value, max));
  const height = (clamped / max) * CHART_PLOT_HEIGHT;
  // A zero-height rect is invisible, which reads as "no bar" rather than "a day
  // with none": floored at 1px so an actual measurement is still a mark.
  return clamped > 0 ? Math.max(height, 1) : 0;
}

/** The x of the i-th of n points, evenly spaced across the plot. */
export function plotX(index: number, count: number): number {
  const span = Math.max(count - 1, 1);
  return CHART_PAD_X + (index / span) * (CHART_WIDTH - 2 * CHART_PAD_X);
}

/** A ratio in [0,1] -> the filled width of the ratio bar. The HIT share, never
 *  its complement: the percentage printed beside the bar is the hit rate. */
export function ratioWidth(ratio: number): number {
  if (!Number.isFinite(ratio)) return 0;
  return Math.max(0, Math.min(1, ratio)) * RATIO_WIDTH;
}
